import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LIFECYCLE_PHASE_LABELS,
  PRODUCTION_FORMAT_LABELS,
  TAB_SLUGS,
} from '@scoutaround/script-breakdown-shared';

import type { WorkflowStatus } from '@scoutaround/script-breakdown-shared';

import { api, type Project, type ProjectSetup } from '../lib/api.js';
import { Icon } from './Icon.js';

/**
 * Every page of the guided path. `start` and `scenes` are the shared
 * preparation; `choose` is the choice between the two workflows; `breakdown`
 * and `explore` are the Locations workflow's pages (their URLs predate the
 * split and are kept, being bookmarks), `elements` the detailed breakdown's.
 */
export type SetupStep = 'start' | 'scenes' | 'choose' | 'breakdown' | 'explore' | 'elements';

/** The two shared steps, in the order the producer walks them. `short` is what a phone shows. */
export const PREP_STEPS: readonly { id: 'start' | 'scenes'; label: string; short: string }[] = [
  { id: 'start', label: 'Add script', short: 'Script' },
  { id: 'scenes', label: 'Review & confirm scenes', short: 'Scenes' },
];

/** Every value the server's `nextStep` takes, with the words for it. */
export const SETUP_STEPS: readonly { id: 'start' | 'scenes' | 'choose'; label: string; short: string }[] = [
  ...PREP_STEPS,
  { id: 'choose', label: 'Choose your next workflow', short: 'Next' },
];

export type WorkflowId = 'locations' | 'breakdown';

/**
 * The two workflows after the shared steps, and where each one starts. Neither
 * is a step of the other: either may come first, and finishing one leaves the
 * other exactly where it was (owner brief, 2026-10-29).
 */
export const WORKFLOWS: readonly { id: WorkflowId; label: string; entry: SetupStep }[] = [
  { id: 'locations', label: 'Locations', entry: 'breakdown' },
  { id: 'breakdown', label: 'Detailed breakdown', entry: 'elements' },
];

/** Which workflow a page belongs to; null for the shared steps and the choice. */
export function workflowOf(step: SetupStep): WorkflowId | null {
  if (step === 'breakdown' || step === 'explore') return 'locations';
  if (step === 'elements') return 'breakdown';

  return null;
}

/** The scenes are confirmed, so both workflows are open. */
export function workflowsOpen(setup: ProjectSetup | null): boolean {
  return setup !== null && setup.hasScript && (setup.nextStep === 'choose' || setup.nextStep === null);
}

/**
 * THE GUIDED FIRST PASS, AS ONE SHELL.
 *
 * Laid over the workspace the production already has: two shared steps — add
 * the script, confirm its scenes — and then two independent workflows, Locations
 * and the detailed breakdown, in either order (owner brief, 2026-10-29). This is
 * the chrome every page renders inside — the production chip, the tracker, the
 * way back — so the tracker rule lives in one place.
 *
 * THE TRACKER IS THE SERVER'S ANSWER. `api.projectSetup` derives "how far" from
 * the same rows the workspace reads, plus the bookmarks. A shared step is DONE
 * when that answer says so; a workflow carries the status the server computed
 * from its own rows. Nothing here counts anything itself.
 *
 * `Skip for now` IS NOT `Continue`. Skip opens the workspace and writes
 * nothing — no bookmark, no record — so reopening the production shows
 * "Continue setup" at the same unfinished step. Continue is each step's own
 * primary action and is what moves the bookmark.
 *
 * A step that keeps its way out in its own action bar (step 1: "Skip for now"
 * bottom-left, the primary bottom-right) passes `stepOwnsExit`, and the shell
 * then draws none — one way out per step, wherever it sits.
 */
export function SetupFlow({
  project,
  step,
  onBackToProject,
  onSkip,
  onNavigate,
  stepOwnsExit = false,
  narrow = false,
  children,
}: {
  project: Project;
  step: SetupStep;
  onBackToProject: () => void;
  /** Present only on the steps where leaving without doing anything is a real
   *  choice (adding the script, passing the overview). */
  onSkip?: () => void;
  /** A DONE step on the tracker opens it again — going back is always allowed. */
  onNavigate?: (step: SetupStep) => void;
  /** The step renders its own way out; the header draws neither Skip nor Back. */
  stepOwnsExit?: boolean;
  /** A form-width page (step 1): the chip and tracker line up with its ~960px column. */
  narrow?: boolean;
  children: ReactNode;
}) {
  const setup = useQuery({
    queryKey: ['project-setup', project.id],
    queryFn: () => api.projectSetup(project.id),
  });

  return (
    <div
      data-testid="setup-flow"
      data-step={step}
      style={{
        padding: 'clamp(14px, 2.4vw, 26px) clamp(16px, 4vw, 40px) 72px',
        // The editors (scenes, requirements, locations) use the width a desk has.
        maxWidth: narrow ? 1040 : 1440,
        margin: '0 auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        {/* The production this is about, in the words the workspace uses for it. */}
        <div data-testid="setup-project-chip" style={{ minWidth: 0 }}>
          <p className="dh" style={{ fontSize: 22 }}>
            {project.title}
          </p>
          <p className="mm" style={{ fontSize: 12.5, margin: '4px 0 0' }}>
            {PRODUCTION_FORMAT_LABELS[project.format]}
            {' · '}
            {LIFECYCLE_PHASE_LABELS[project.lifecyclePhase]}
          </p>
        </div>

        {/* ONE WAY OUT PER STEP. Skip and Back both opened the workspace; two
            buttons for one action read as two different actions. Where
            skipping is a real choice the button says so; elsewhere it is
            simply the way back. */}
        {stepOwnsExit ? null : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {onSkip ? (
            <button
              type="button"
              className="ctl"
              data-testid="setup-skip"
              title="Opens the project. You can continue setup at any time."
              onClick={onSkip}
            >
              Skip for now →
            </button>
          ) : (
            <button
              type="button"
              className="ctl"
              data-testid="setup-back-to-project"
              onClick={onBackToProject}
            >
              Back to project →
            </button>
          )}
        </div>
        )}
      </div>

      <SetupTracker current={step} setup={setup.data ?? null} onNavigate={onNavigate} />

      <div style={{ marginTop: 20 }}>{children}</div>
    </div>
  );
}

/**
 * Where a shared step stands, from the server's answer.
 *
 * `done` is the server's — never "the producer got past it". A step can be
 * current and done at once (scenes reopened after they were confirmed); it then
 * reads done, with the tick, because that is true.
 */
export function stepState(
  id: 'start' | 'scenes',
  current: SetupStep,
  setup: ProjectSetup | null,
): 'done' | 'current' | 'upcoming' {
  if (setup !== null && isDone(id, setup)) return 'done';
  if (id === current) return 'current';

  return 'upcoming';
}

function isDone(id: 'start' | 'scenes', setup: ProjectSetup): boolean {
  if (id === 'start') return setup.hasScript;

  // The bookmark, or the proof: a production that finished its requirements, or
  // went on past them, before scene validation existed has seen its scenes.
  return setup.scenesConfirmedAt !== null || setup.reviewComplete || setup.breakdownContinuedAt !== null;
}

const WORKFLOW_STATUS_LABEL: Record<WorkflowStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
};

function SetupTracker({
  current,
  setup,
  onNavigate,
}: {
  current: SetupStep;
  setup: ProjectSetup | null;
  onNavigate?: (step: SetupStep) => void;
}) {
  const open = workflowsOpen(setup);
  const active = workflowOf(current);

  return (
    <nav aria-label="Production setup" className="setup-path" data-testid="setup-tracker">
      <div className="setup-path-group setup-path-prep">
        <p className="setup-path-kicker">Prepare</p>
        <ol className="setup-tracker" data-testid="setup-prep">
          {PREP_STEPS.map((s, i) => {
            const state = stepState(s.id, current, setup);
            const body = (
              <>
                <span className="setup-mark" aria-hidden="true">
                  {state === 'done' ? <Icon name="check" size={13} strokeWidth={2.6} /> : i + 1}
                </span>
                {/* Two spellings, one visible at a time: the phone shows the short one. */}
                <span className="setup-label">
                  <span className="setup-label-long">{`${String(i + 1)}. ${s.label}`}</span>
                  <span className="setup-label-short" aria-hidden="true">{`${String(i + 1)}. ${s.short}`}</span>
                </span>
              </>
            );

            return (
              <li
                key={s.id}
                data-testid={`setup-step-${s.id}`}
                data-state={state}
                aria-current={s.id === current ? 'step' : undefined}
              >
                {state === 'done' && onNavigate && s.id !== current ? (
                  <button
                    type="button"
                    className="setup-step-link"
                    data-testid={`setup-step-link-${s.id}`}
                    aria-label={`Back to ${s.label}`}
                    onClick={() => {
                      onNavigate(s.id);
                    }}
                  >
                    {body}
                  </button>
                ) : (
                  body
                )}
                {i < PREP_STEPS.length - 1 ? <span className="setup-rule" aria-hidden="true" /> : null}
              </li>
            );
          })}
        </ol>
      </div>

      {/* THE WORKFLOWS ARE SIDE BY SIDE, NOT A SEQUENCE. A numbered "3. Review
          breakdown → 4. Explore locations" told producers the breakdown was a
          prerequisite of locations; neither is the other's. Each carries the
          status its own rows give it, and opens once the scenes are confirmed. */}
      <div className="setup-path-group setup-path-workflows">
        <p className="setup-path-kicker">
          Then, in any order
          {open && current !== 'choose' && onNavigate ? (
            <button
              type="button"
              className="setup-path-all"
              data-testid="setup-choose-link"
              onClick={() => {
                onNavigate('choose');
              }}
            >
              Compare both
            </button>
          ) : null}
        </p>
        <ul className="setup-workflows" data-testid="setup-workflows">
          {WORKFLOWS.map((w) => {
            const status = setup?.workflows[w.id] ?? null;
            const here = active === w.id;
            const body = (
              <>
                <span className="setup-workflow-name">{w.label}</span>
                <span className="setup-workflow-status" data-testid={`setup-workflow-status-${w.id}`}>
                  {open ? (status === null ? '' : WORKFLOW_STATUS_LABEL[status]) : 'After the scenes'}
                </span>
              </>
            );

            return (
              <li
                key={w.id}
                className="setup-workflow"
                data-testid={`setup-workflow-${w.id}`}
                data-current={here ? '1' : '0'}
                data-status={open ? (status ?? '') : 'LOCKED'}
                aria-current={here ? 'step' : undefined}
              >
                {open && !here && onNavigate ? (
                  <button
                    type="button"
                    className="setup-workflow-link"
                    data-testid={`setup-workflow-link-${w.id}`}
                    aria-label={`Open ${w.label}${status === null ? '' : ` — ${WORKFLOW_STATUS_LABEL[status].toLowerCase()}`}`}
                    onClick={() => {
                      onNavigate(w.entry);
                    }}
                  >
                    {body}
                  </button>
                ) : (
                  <span className="setup-workflow-body">{body}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

/** The workspace address for a setup step's exits. */
export function workspacePath(address: string, tab: keyof typeof TAB_SLUGS = 'OVERVIEW'): string {
  return `/projects/${address}/${TAB_SLUGS[tab]}`;
}
